import React, { lazy, Suspense, useEffect } from 'react';
import { pipe } from '../operators';
import styles from './Markdown.module.scss';
import { HighlightItem } from '../../models/HighlightItem';
import { ActivityItemMetadata } from '../../models/Activity';

const ReactMarkdown = lazy(() => import('react-markdown'));

// Custom parsed entities
const renderAdded = ({ children }) => (
  <span className={`${styles.after} diff-added`}>{children}</span>
);

const renderRemoved = ({ children }) => (
  <span className={`${styles.before} diff-removed`}>{children}</span>
);

const renderOrderedList = ({ children, ...props }) => (
  <p>
    {props.start ? props.start : '1'}. {children}
  </p>
);

const renderWithoutEnclosingTag = ({ children }) => <>{children}</>;

const remarkClay = (visit) => () => (tree) => {
  visit(tree, ['textDirective'], (node) => {
    node.data = {
      hName: node.name,
      hProperties: node.attributes,
      ...node.data,
    };
    return node;
  });
};

const loadPluginCode = async () => {
  const [remarkGfm, remarkDirective, unistUtilVisit] = await Promise.all([
    import('remark-gfm'),
    import('remark-directive'),
    import('unist-util-visit'),
  ]);
  return [remarkGfm.default, remarkDirective.default, remarkClay(unistUtilVisit.visit)];
};

const renderDiffLink = ({ children, ...props }) => (
  <a onClick={(e) => e.stopPropagation()} {...props}>
    <span className={styles.link}>{children}</span>
  </a>
);

export function renderDiffMarkdown(content: string) {
  const [plugins, setPlugins] = React.useState([]);

  useEffect(() => {
    const loadPlugins = async () => {
      const ps = await loadPluginCode();
      setPlugins(ps);
    };
    loadPlugins();
  }, []);

  return (
    <Suspense fallback={<div />}>
      <ReactMarkdown
        children={content}
        linkTarget="_blank"
        remarkPlugins={plugins}
        components={{
          a: renderDiffLink,
          ol: renderOrderedList,
          li: renderWithoutEnclosingTag,
          p: renderWithoutEnclosingTag,
          added: renderAdded,
          removed: renderRemoved,
        }}
      />
    </Suspense>
  );
}

export function renderSocialDiffMarkdown(activityItemMetadata: Partial<ActivityItemMetadata>) {
  const { content, before, after } = activityItemMetadata || {};

  if (before && after) {
    return (
      <p>
        <span className={styles.before}>{renderDiffMarkdown(before)}</span>
        <span className={styles.after}>{renderDiffMarkdown(after)}</span>
      </p>
    );
  }

  return <p>{renderDiffMarkdown(content)}</p>;
}

export default function renderSharedContactHighlightsMarkdown(item: HighlightItem) {
  const [plugins, setPlugins] = React.useState([]);
  const useCustomParsing = item.source == 'twitter';
  // const remarkPlugins = useCustomParsing ? [remarkGfm, remarkDirective, remarkClay] : [remarkGfm];

  useEffect(() => {
    const loadPlugins = async () => {
      const ps = await loadPluginCode();

      if (useCustomParsing) {
        return setPlugins(ps);
      } else {
        return setPlugins([ps[0]]);
      }
    };
    loadPlugins();
  }, [useCustomParsing]);

  return (
    <Suspense fallback={<div />}>
      <ReactMarkdown
        children={item.content}
        linkTarget="_blank"
        components={{
          a: renderDiffLink,
          added: renderAdded,
          removed: renderRemoved,
        }}
        remarkPlugins={plugins}
      />
    </Suspense>
  );
}

const mapDelimiterToShortcode = (delimiter: string, shortcodeType: string) => {
  const escapedDelimiter = '\\' + delimiter.split('').join('\\');
  const pattern = `${escapedDelimiter}.+?${escapedDelimiter}`;
  const regex = RegExp(pattern);
  const delimLength = delimiter.length;

  return (md: string) => {
    let mdWithReplacements = md;
    let lastMatch = null;

    while ((lastMatch = regex.exec(mdWithReplacements)) !== null) {
      const match = lastMatch[0];
      const replacement = `:${shortcodeType}[${match.slice(delimLength, -delimLength)}]`;
      const idx = lastMatch.index;
      const beforeMatch = mdWithReplacements.slice(0, idx);
      const afterMatch = mdWithReplacements.slice(idx + match.length);
      mdWithReplacements = beforeMatch + '&#x200B;' + replacement + afterMatch;
    }

    return mdWithReplacements;
  };
};

const insertRemoved = mapDelimiterToShortcode('~~', 'removed');
const insertAdded = mapDelimiterToShortcode('**', 'added');
export const preProcessDiffMD = (md: string): string => pipe(insertRemoved, insertAdded)(md);
